import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, InputBase, makeStyles } from '@material-ui/core';
import { cloneDeep, debounce, isFunction } from 'lodash';
import moment from 'moment';
import { DatePicker } from '@material-ui/pickers';
import { TELEVISIT_STATUS } from '../../../comm/constant';
import { SearchIcon } from '../../../components/Icon';
import { teleVisitAPI } from '../../../apis';
import { ReactComponent as TimeClock } from '../../../static/images/time_clock.svg';
import { ReactComponent as TeleSearchDate } from '../../../static/images/tele_search_date.svg';
import { i18n } from '../../../language';
import { onKeyUpEnterOrSpace } from '../../../utils';

const useStyles = makeStyles((theme) => ({
  search: ({ width, height, m, isFocus }) => ({
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.font.white,
    width: width ?? '18.5rem',
    height: height ?? '2.375rem',
    display: 'flex',
    justifyContent: 'space-between',
    margin: m,
    border: isFocus ? '0.0625rem solid #538fff' : '0.0625rem solid #ddd',
    '@media (max-width: 80rem)': {
      width: '100%',
    },
  }),
  input: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.919rem',
    letterSpacing: '0',
    color: theme.palette.font.sub,
    fontWeight: '500',
    // margin: '0 0.938rem 0 0.938rem',
    width: '100%',
    height: '100%',
  },
  iconbtn: {
    padding: '0',
    borderRadius: '7.8px',
    backgroundColor: '#4c93ff',
    '&:hover': {
      backgroundColor: 'unset',
    },
  },
  searchbtn: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
    padding: '0.438rem 0.438rem 0.438rem 0.438rem',
    cursor: 'pointer',
    // margin: '0.313rem 0.313rem 0.313rem 0',
    backgroundColor: '#4c93ff',
    borderRadius: '0.4875rem',
    fontFamily: 'NotoSans-Medium',
    whiteSpace: 'nowrap',
  },
  searchIcon: {
    color: theme.palette.font.white,
    fontSize: '1.125rem',
    marginRight: '0.275rem',
    marginTop: '0.1rem',
  },
  closeIcon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  time: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.12rem',
    color: '#979797',
    whiteSpace: 'pre',
  },
  optionTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#979797',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionSubTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#979797',
  },
}));

const SearchBar = ({
  onClick,
  onChange = () => {},
  placeholder,
  inputID,
  buttonID,
  onFocus,
  onBlur,
  m = '0 0.8rem 0 0',
  p,
  width,
  height,
  reset,
  searchText,
  onClickOption = () => {},
  endDate,
  handleAdvanceSearch,
}) => {
  const [value, setValue] = useState('');
  const [searchResult, setSearchResult] = useState({});

  const [dateDialog, setDateDialog] = useState(false);
  const rangeDateFormat = 'YYYY-MM-DD';
  const oriRangeDate = useMemo(
    () => ({
      startDate: moment(endDate).subtract(12, 'month').add(1, 'd').format(rangeDateFormat),
      endDate: moment(endDate).format(rangeDateFormat),
    }),
    [endDate]
  );
  const [rangeDate, setRangeDate] = useState(cloneDeep(oriRangeDate));
  const [openOption, setOpenOption] = useState(false);

  const oriPage = {
    pageNo: 1,
    pageSize: 15,
  };
  const [page, setPage] = useState({
    ...oriPage,
  });
  const [itemList, setItemList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const inputBase = useRef(null);
  const listWrap = useRef(null);
  const dialog = useRef(null);
  const classes = useStyles({ width, height, m, isFocus: openOption });

  useEffect(() => {
    setRangeDate(cloneDeep(oriRangeDate));
  }, [oriRangeDate]);

  const rangeDateText = useMemo(() => {
    const format = 'YYYY-MM-DD';
    const startDate = rangeDate.startDate && moment(rangeDate.startDate).format(format);
    const endDate = rangeDate.endDate && moment(rangeDate.endDate).format(format);

    return {
      startDate,
      endDate,
    };
  }, [rangeDate]);

  const isMaxCount = useMemo(() => itemList.length >= searchResult.count, [itemList, searchResult]);

  const resetPage = useCallback(() => {
    setPage({ ...oriPage });
  }, []);

  const search = useCallback(
    async (params) => {
      try {
        params = {
          keyword: value,
          ...page,
          ...params,
          startDate: moment(rangeDate.startDate, 'yyyy-MM-DD').format('x'),
          endDate: moment(rangeDate.endDate, 'yyyy-MM-DD').add('d', 1).startOf('day').format('x'),
        };
        if (isMaxCount && params.pageNo > 1) return;
        setIsLoading(true);

        const res = await teleVisitAPI.getTimeslotSearch(params, { isHideLoading: true });
        const data = (
          cloneDeep(res.result?.data || [])?.map((data) => {
            const { bedNo, patientNameCh, patientNameEn } = data;
            data.value = data.bookId;
            data.text = `${bedNo || ''} ${bedNo ? '-' : ''}  ${patientNameEn || ''} ${patientNameCh || ''}`;

            return data;
          }) || []
        ).filter((val) => val);

        setSearchResult(res.result);

        if (params.pageNo <= 1) {
          listWrap.current?.scroll?.(0, 0);
          setItemList([...data]);
        } else {
          setItemList([...itemList, ...data]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [value, itemList, rangeDate]
  );

  // reset data
  useEffect(() => {
    setValue('');
  }, [reset]);

  const debounceSearchHandle = useRef();
  debounceSearchHandle.current = async (...arg) => {
    (async () => {
      await search(...arg);
    })();
  };

  // Stabilization function;
  const debouncedSearch = useCallback(
    debounce((...arg) => debounceSearchHandle.current(...arg), 500),
    [debounceSearchHandle]
  );

  const debounceScrollHandle = useRef();
  debounceScrollHandle.current = (e) => {
    // + 1 deals with rem calculation errors
    const isInBottom = e.target.scrollTop + e.target.clientHeight + 1 >= e.target.scrollHeight;
    const newPage = {
      ...page,
      pageNo: page.pageNo + 1,
    };

    if (isInBottom) {
      debouncedSearch({
        ...newPage,
      });
      setPage(newPage);
    }
  };
  const debounceScroll = useCallback(
    debounce((...arg) => debounceScrollHandle.current(...arg), 100),
    []
  );

  // Eliminate side effects
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // input onChange event
  const onChangeHandler = useCallback((event) => {}, [debouncedSearch]);

  // key enter event
  const onKeyDownHandler = useCallback(
    (event) => {
      if ((event.code === 'Enter' || event.code === 'NumpadEnter') && isFunction(onClick)) {
        onClick(value);
      }
    },
    [onClick, value]
  );

  const clickSearchDate = useCallback(() => {
    setDateDialog(true);
  }, []);

  const handleChangeRangeDate = useCallback(
    (value, key) => {
      const newRangeDate = {
        ...rangeDate,
        [key]: value.format(rangeDateFormat),
      };

      if (key === 'startDate') {
        const startDate = moment(value);
        const endDate = moment(rangeDate.endDate);

        endDate.isBefore(startDate) && (newRangeDate.endDate = startDate.format(rangeDateFormat));
      }

      setRangeDate(newRangeDate);
    },
    [rangeDate]
  );

  const handleInput = useCallback(
    (e) => {
      const value = e?.target?.value || '';
      debouncedSearch({
        keyword: value,
        ...oriPage,
      });
      setValue(value);
      resetPage();
    },
    [debouncedSearch, page]
  );

  const handleDialogClose = useCallback(() => {
    debouncedSearch({
      ...oriPage,
    });
    resetPage();
    setDateDialog(false);
  }, [debouncedSearch]);

  const handleClickOption = useCallback(
    async (data, ...arg) => {
      setOpenOption(false);
      setValue('');
      setSearchResult([]);
      setItemList([]);
      onClickOption(data);
    },
    [onClickOption]
  );

  const noBlurInput = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  return (
    <Box className={classes.search} m={m} p={p} position="relative">
      <Box ref={inputBase} paddingLeft="0.938rem" paddingRight="0.625rem" width="100%">
        <InputBase
          className={classes.input}
          id={inputID}
          value={value}
          onChange={onChangeHandler}
          placeholder={placeholder}
          onKeyDown={onKeyDownHandler}
          onFocus={() => setOpenOption(true)}
          onBlur={(...arg) => {
            setOpenOption(false);
            onBlur?.(...arg);
            setOpenOption(false);
            setValue('');
            setSearchResult([]);
            setItemList([]);
          }}
          onInput={handleInput}
          open={openOption}
          height="100%"
          border="0px"
          endAdornment={
            <Box
              onMouseDown={noBlurInput}
              height="100%"
              padding="0.313rem 0 0.313rem 0.625rem"
              display="flex"
              gridColumnGap="0.5rem"
            >
              <Box display="flex" onClick={clickSearchDate}>
                <Box display="flex" alignItems="center" paddingLeft="0.625rem" borderLeft="1px solid #d9d9d9">
                  <Box whiteSpace="nowrap">
                    {rangeDateText.startDate} to {rangeDateText.endDate}
                  </Box>
                  <Box
                    marginLeft="0.828rem"
                    // height="100%"
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <TeleSearchDate />
                  </Box>
                </Box>
              </Box>

              <Box height="100%" display="block">
                <div
                  className={classes.searchbtn}
                  id={buttonID}
                  onClick={handleAdvanceSearch(true, {})}
                  tabIndex={0}
                  onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleAdvanceSearch(true, {}))}
                >
                  <SearchIcon className={classes.searchIcon} />
                  {searchText}
                </div>
              </Box>
            </Box>
          }
        />
        {openOption && Boolean(itemList.length) && (
          <Box
            ref={listWrap}
            onMouseDown={noBlurInput}
            position="absolute"
            bottom="-1px"
            left="0"
            borderRadius="8px"
            boxShadow="0 1px 6px 0 rgba(0, 0, 0, 0.12)"
            border="solid 1px #e4e7eb"
            width="100%"
            zIndex="999"
            overflow="auto"
            maxHeight="20rem"
            padding="0.75rem 0"
            sx={{
              transform: 'translateY(100%)',
              background: '#fff',
            }}
            onScroll={debounceScroll}
          >
            {itemList.map((item) => (
              <FilterOption onClick={() => handleClickOption(item)} key={item.value} item={item} inputValue={value} />
            ))}
            {isLoading && Boolean(value) && <Box textAlign="center">loading...</Box>}
          </Box>
        )}
      </Box>
      {dateDialog && (
        <Dialog ref={dialog} maxWidth={false} open={true} onClose={handleDialogClose}>
          <Box display="flex" onClick={() => false}>
            <DatePicker
              onChange={(value) => handleChangeRangeDate(value, 'startDate')}
              variant="static"
              value={rangeDate.startDate}
              minDate={moment(rangeDate.endDate).subtract(1, 'Y').add(1, 'd')}
              maxDate={rangeDate.endDate}
            />
            <DatePicker
              minDate={rangeDate.startDate}
              maxDate={moment(rangeDate.startDate).add(1, 'Y').subtract(1, 'd')}
              onChange={(value) => handleChangeRangeDate(value, 'endDate')}
              variant="static"
              value={rangeDate.endDate}
            />
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

function FilterOption(props) {
  const classes = useStyles();
  const { item, inputValue, ...rest } = props;
  const date = useMemo(() => {
    const timeslotDate = moment(Number(item.timeslotDate)).format('YYYY-MM-DD');
    const timeslotEndDate = item.timeslotEndDate
      ? `${moment(Number(item.timeslotEndDate)).format('YYYY-MM-DD')}  `
      : '';
    if (timeslotEndDate !== '') {
      return `${timeslotDate || ''}  ${item.startTime || ''}  -  ${timeslotEndDate}${item.endTime || ''}`;
    }
    return `${timeslotDate || ''}  ${item.startTime || ''}  -  ${item.endTime || ''}`;
  }, []);

  const text = useMemo(() => {
    const text = item.text;

    return inputValue
      ? text.replace(
          new RegExp(`(${inputValue})`, 'gi'),
          '<span style="color: #000; font-family: NotoSans-Bold;">$1</span>'
        )
      : text;
  }, [inputValue, item]);

  const ref = useMemo(() => {
    const text = String(item.value);

    return inputValue
      ? text.replace(
          new RegExp(`(${inputValue})`, 'gi'),
          '<span style="color: #000; font-family: NotoSans-Bold;">$1</span>'
        )
      : text;
  }, [inputValue, item]);

  const status = useMemo(() => {
    let statusText = '';
    switch (item.timeslotBookingStatusEnum) {
      case TELEVISIT_STATUS.CONFIRMED:
        statusText = i18n.t('TELEVISIT_CONFIRMED_SCHEDULED');
        break;
      case TELEVISIT_STATUS.INPROGRESS:
        statusText = i18n.t('TELEVISIT_VISIT_IN_PROGRESS');
        break;
      case TELEVISIT_STATUS.COMPLETED:
        statusText = i18n.t('TELEVISIT_VISIT_COMPLETED');
        break;
      case TELEVISIT_STATUS.ROBOT_START_PROCESSING_BOOKING:
        statusText = i18n.t('TELEVISIT_ROBOT_STARTS_PROCESS_THE_BOOKING');
        break;
      case TELEVISIT_STATUS.AVAILABLE:
        statusText = i18n.t('TELEVISIT_AVAILABLE');
        break;
      case TELEVISIT_STATUS.RESERVED:
        statusText = i18n.t('TELEVISIT_VERIFYING');
        break;
      case TELEVISIT_STATUS.UNAVAILABLE:
        statusText = i18n.t('TELEVISIT_UNAVAILABLE');
        break;
      case TELEVISIT_STATUS.CANCEL:
        statusText = i18n.t('TELEVISIT_CANCELLED');
        break;
      case TELEVISIT_STATUS.COMPLETED_MANUALLY:
        statusText = i18n.t('TELEVISIT_VISIT_COMPLETED_MANUALLY');
        break;
      case TELEVISIT_STATUS.REJECTED:
        statusText = i18n.t('TELEVISIT_VISIT_REJECTED');
        break;
      default:
        break;
    }
    return statusText.replace('<br', '').replace('/>', '');
  }, [inputValue, item]);

  return (
    <Box
      color="#b0b0b0"
      fontWeight="400"
      width="100%"
      height="100%"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      padding="0.75rem"
      sx={{
        '&:hover': {
          background: '#dde9ff',
        },
        cursor: 'pointer',
      }}
      {...rest}
    >
      <Box display="flex" flexDirection="column" whiteSpace="nowrap" minWidth="18rem" width="100%">
        <Box className={classes.optionTitle} dangerouslySetInnerHTML={{ __html: text }} />
        <Box
          className={classes.optionSubTitle}
          dangerouslySetInnerHTML={{
            __html: `${i18n.t('TELEVISIT_SEARCH_REF', { value: ref })} - ${status}`,
          }}
        />
      </Box>
      <Box display="flex">
        {item.hocFlag === 1 && (
          <Box display="flex" alignItems="center" minWidth="4.0rem">
            ({i18n.t('TELEVISIT_ADHOC_FLAG')})
          </Box>
        )}
      </Box>
      <Box display="flex">
        <Box marginRight="0.313rem" display="flex" alignItems="center">
          <TimeClock style={{ height: '1.05rem', width: '1.05rem' }} />
        </Box>
        <Box marginRight="0.313rem" display="flex" alignItems="center" flexDirection="column" whiteSpace="nowrap">
          <Box className={classes.time}>{date}</Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SearchBar;
